import React, { useId } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import './StatusIcon.module.scss'

const TOOLTIP_DELAY = { show: 250, hide: 400 }

const propTypes = {
  /** Font Awesome icon class, e.g. "fas fa-users". */
  icon: PropTypes.string.isRequired,
  /** Accessible label — used as the aria-label, and as the tooltip when `tooltip` is omitted. */
  label: PropTypes.string.isRequired,
  /** Tooltip content; falls back to `label` when omitted. */
  tooltip: PropTypes.node,
  /** When false the icon is dimmed to read as "not set" and is hidden from assistive tech. */
  active: PropTypes.bool,
}

/**
 * Small square status icon (22x22) — the non-interactive member of the icon family. Unlike
 * IconButton it renders a plain `<span role="img">` (no button/link semantics): use it to indicate
 * state, not to trigger an action. Colour comes from the `--aug-act-icon` theme token, so it follows
 * light/dark mode instead of a baked-in hex. Dim it with `active={false}` to read as "not set"; an
 * inactive icon marks an absent state, so it is hidden from screen readers — only the icons that
 * actually apply are announced (avoids a dimmed "Windows" being read on a Mac agent).
 */
function StatusIcon({ icon, label, tooltip = null, active = true }) {
  const tooltipId = useId()
  const overlayContent = tooltip ?? label

  const control = (
    <span
      role="img"
      aria-label={label}
      aria-hidden={active ? undefined : 'true'}
      className={clsx('Uic-StatusIcon-base', !active && 'Uic-StatusIcon-muted')}
    >
      <i className={icon} aria-hidden="true" />
    </span>
  )

  if (!overlayContent) {
    return control
  }

  return (
    <OverlayTrigger placement="top" delay={TOOLTIP_DELAY} overlay={<Tooltip id={tooltipId}>{overlayContent}</Tooltip>}>
      {control}
    </OverlayTrigger>
  )
}

StatusIcon.propTypes = propTypes

export default React.memo(StatusIcon)
